import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { OVERLAY_ATTRIBUTE } from '../board-grid/useZoomPan';
import type { LiveScanState } from './useLiveScan';

/**
 * Over the photo while it is read for holds (`useLiveScan`): the picture is
 * already there to look at, and this says it is not yet one to press on.
 *
 * It covers the whole block, buttons and all, and takes every press: a circle
 * drawn now would be thrown away the moment the scan's holds arrive, since a
 * scanned wall is chosen from rather than drawn on. The bar fills tile by tile
 * — the hold model's calls — and then stands full while the holds are worked
 * out of them. `onSkip` gives up waiting: the photo stays, to be circled by
 * hand.
 */
export function LiveScanOverlay({ state, onSkip }: { state: LiveScanState; onSkip: () => void }) {
  const { t } = useTranslation();
  const detail =
    state.phase === 'scanning' && state.done != null && state.total != null
      ? t('climbEditor.liveScan.tiles', { done: state.done, total: state.total })
      : t(`climbEditor.liveScan.${state.phase}`);

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-black/45 p-6 backdrop-blur-[1px]"
      data-testid="live-scan-overlay"
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
    >
      <div className="w-full max-w-xs space-y-3 rounded-2xl bg-background/95 p-4 text-center shadow-xl">
        <p className="flex items-center justify-center gap-2 text-sm font-medium">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {t('climbEditor.liveScan.processing')}
        </p>
        <div
          role="progressbar"
          aria-label={t('climbEditor.liveScan.processing')}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={state.fraction == null ? undefined : Math.round(state.fraction * 100)}
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        >
          {state.fraction == null ? (
            // Nothing to count yet — the photo going up, or the job waiting
            // for the worker — so the bar says "under way" rather than a share.
            <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/60" />
          ) : (
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-500"
              style={{ width: `${Math.round(state.fraction * 100)}%` }}
            />
          )}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="live-scan-detail">
          {detail}
        </p>
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onSkip}>
          {t('climbEditor.liveScan.skip')}
        </Button>
      </div>
    </div>
  );
}
